import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  IconButton,
  Card,
  CardContent,
  Link,
  Alert,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  Edit,
  Save,
  Cancel,
  ContentCopy,
  PlayArrow,
  Download,
  Image as ImageIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import ChangeRequestDetailsView from './ChangeRequestDetailsView';

const FeedbackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    component: '',
    environment: '',
    version: '',
    assignedTo: 'Support Team'
  });
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [screenshotDialogOpen, setScreenshotDialogOpen] = useState(false);
  const [amApprovalNotes, setAmApprovalNotes] = useState('');
  const [decidingAmApproval, setDecidingAmApproval] = useState(false);

  useEffect(() => {
    fetchFeedback();
  }, [id]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/v1/feedback/${id}`);
      setFeedback(response.data);
      setEditData({
        status: response.data.status,
        component: response.data.component || '',
        environment: response.data.environment || '',
        version: response.data.version || '',
        assignedTo: response.data.assignedTo || 'Support Team'
      });
    } catch (error) {
      console.error('Error fetching feedback:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load feedback';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = () => {
    if (!feedback || !user) return false;
    const isSubmitter = feedback.submittedBy && feedback.submittedBy._id === user.id;
    return isSubmitter || user.role === ROLES.SUPER_ADMIN || user.role === ROLES.MIS;
  };

  const canComment = () => {
    return feedback && feedback.status !== 'Closed';
  };

  const canDecideAmApproval = () => {
    if (!feedback || !user) return false;
    const isAmRole = user.role === ROLES.ACCOUNTABLE_MANAGER || user.role === ROLES.SUPER_ADMIN;
    return (
      isAmRole &&
      feedback.type === 'Change Request Form' &&
      feedback.amApproval?.required &&
      feedback.amApproval?.status === 'PENDING'
    );
  };

  const handleAmApproval = async (decision) => {
    try {
      setDecidingAmApproval(true);
      await axios.post(`/api/v1/feedback/${id}/am-approval`, {
        decision,
        notes: amApprovalNotes.trim() || undefined
      });
      showToast(
        decision === 'APPROVED' ? 'Change request approved' : 'Change request rejected',
        'success'
      );
      setAmApprovalNotes('');
      fetchFeedback();
    } catch (error) {
      console.error('Error deciding AM approval:', error);
      showToast(error.response?.data?.message || 'Failed to update AM approval', 'error');
    } finally {
      setDecidingAmApproval(false);
    }
  };
  const getSubmitterFullName = () => {
    if (!feedback?.submittedBy) return '';
    return `${feedback.submittedBy.firstName || ''} ${feedback.submittedBy.lastName || ''}`.trim();
  };

  const canEditAssignedTo = () => {
    if (!feedback || !user) return false;
    const currentAssignedTo = feedback.assignedTo || 'Support Team';
    const submitterFullName = getSubmitterFullName();
    const isSubmitter = feedback.submittedBy && feedback.submittedBy._id === user.id;
    const isMis = user.role === ROLES.MIS;

    if (!submitterFullName) return false;
    if (isMis && currentAssignedTo === 'Support Team') return true;
    if (isSubmitter && currentAssignedTo === submitterFullName) return true;
    return false;
  };


  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({
      status: feedback.status,
      component: feedback.component || '',
      environment: feedback.environment || '',
      version: feedback.version || '',
      assignedTo: feedback.assignedTo || 'Support Team'
    });
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      // Filter out empty strings to avoid validation errors
      const payload = Object.entries(editData).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});
      await axios.put(`/api/v1/feedback/${id}`, payload);
      showToast('Feedback updated successfully', 'success');
      setIsEditing(false);
      fetchFeedback();
    } catch (error) {
      console.error('Error updating feedback:', error);
      showToast(
        error.response?.data?.message || 'Failed to update feedback',
        'error'
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) {
      showToast('Comment cannot be empty', 'error');
      return;
    }

    try {
      setSubmittingComment(true);
      await axios.post(`/api/v1/feedback/${id}/comments`, {
        comment: newComment
      });
      showToast('Comment added successfully', 'success');
      setNewComment('');
      fetchFeedback();
    } catch (error) {
      console.error('Error adding comment:', error);
      showToast(
        error.response?.data?.message || 'Failed to add comment',
        'error'
      );
    } finally {
      setSubmittingComment(false);
    }
  };

  const copyShareableLink = () => {
    // We need to get the public token from the backend or store it
    // For now, we'll construct a basic link
    const link = `${window.location.origin}/feedback/${id}`;
    navigator.clipboard.writeText(link);
    showToast('Link copied to clipboard!', 'success');
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'grey',
      'In Progress': 'info',
      'Fixed': 'success',
      'Closed': 'grey',
      'Reopened': 'warning',
      'On Hold': 'warning'
    };
    return colors[status] || 'grey';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Blocker': 'error',
      'Critical': 'error',
      'High': 'warning',
      'Medium': 'info',
      'Low': 'default'
    };
    return colors[priority] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/feedback')}>
          Back to List
        </Button>
      </Box>
    );
  }

  if (!feedback) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Feedback not found
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/feedback')}>
          Back to List
        </Button>
      </Box>
    );
  }

  const shareableLink = feedback.shareableLink 
    ? `${window.location.origin}${feedback.shareableLink}`
    : `${window.location.origin}/feedback/${feedback._id}`;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Feedback Details</Typography>
        <Button variant="outlined" onClick={() => navigate('/feedback')}>
          Back to List
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* Main Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {feedback.feedbackId}
            </Typography>

            {feedback.amApproval?.required && (
              <Alert
                severity={
                  feedback.amApproval.status === 'APPROVED'
                    ? 'success'
                    : feedback.amApproval.status === 'REJECTED'
                      ? 'error'
                      : 'warning'
                }
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  AM Approval: {feedback.amApproval.status || 'PENDING'}
                </Typography>
                {feedback.amApproval.decidedBy && (
                  <Typography variant="body2">
                    {`Decided by ${feedback.amApproval.decidedBy.firstName || ''} ${feedback.amApproval.decidedBy.lastName || ''}`.trim()}
                    {feedback.amApproval.decidedAt
                      ? ` on ${new Date(feedback.amApproval.decidedAt).toLocaleString()}`
                      : ''}
                  </Typography>
                )}
                {feedback.amApproval.notes && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Notes: {feedback.amApproval.notes}
                  </Typography>
                )}
              </Alert>
            )}

            {canDecideAmApproval() && (
              <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  AM Approval Required
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Approve to move this change request to In Progress, or reject to close it.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Approval notes (optional)"
                  value={amApprovalNotes}
                  onChange={(e) => setAmApprovalNotes(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={decidingAmApproval}
                    onClick={() => handleAmApproval('APPROVED')}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={decidingAmApproval}
                    onClick={() => handleAmApproval('REJECTED')}
                  >
                    Reject
                  </Button>
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Type
                </Typography>
                <Typography variant="body1">{feedback.type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Priority
                </Typography>
                <Chip
                  label={feedback.priority}
                  color={getPriorityColor(feedback.priority)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Severity
                </Typography>
                <Typography variant="body1">{feedback.severity}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={feedback.status}
                  color={getStatusColor(feedback.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Assigned To
                </Typography>
                <Typography variant="body1">{feedback.assignedTo || 'Support Team'}</Typography>
              </Grid>
              <Grid item xs={12}>
                {feedback.changeRequestDetails ? (
                  <ChangeRequestDetailsView details={feedback.changeRequestDetails} />
                ) : (
                  <>
                    <Typography variant="subtitle2" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                      {feedback.description}
                    </Typography>
                  </>
                )}
              </Grid>
            </Grid>

            {/* Voice Recording */}
            {feedback.voiceRecordingUrl && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Voice Recording
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <audio 
                    controls 
                    preload="metadata"
                    crossOrigin="anonymous"
                    src={
                      feedback.voiceRecordingUrl.startsWith('http') 
                        ? feedback.voiceRecordingUrl 
                        : `${window.location.origin}${feedback.voiceRecordingUrl.startsWith('/') ? '' : '/'}${feedback.voiceRecordingUrl}`
                    } 
                    style={{ width: '100%', maxWidth: '500px' }}
                    onError={(e) => {
                      const audioElement = e.target;
                      const error = audioElement.error;
                      let errorMessage = 'Failed to load audio file.';
                      
                      if (error) {
                        switch (error.code) {
                          case error.MEDIA_ERR_ABORTED:
                            errorMessage = 'Audio playback was aborted.';
                            break;
                          case error.MEDIA_ERR_NETWORK:
                            errorMessage = 'Network error while loading audio.';
                            break;
                          case error.MEDIA_ERR_DECODE:
                            errorMessage = 'Audio file could not be decoded. The file may be corrupted or incomplete.';
                            break;
                          case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                            errorMessage = 'Audio format not supported by your browser.';
                            break;
                          default:
                            errorMessage = `Audio error: ${error.message || 'Unknown error'}. The file may be corrupted.`;
                        }
                      }
                      
                      console.error('Audio playback error:', {
                        error,
                        src: audioElement.src,
                        networkState: audioElement.networkState,
                        readyState: audioElement.readyState
                      });
                      showToast(errorMessage, 'error');
                    }}
                    onLoadedMetadata={(e) => {
                      console.log('Audio loaded successfully, duration:', e.target.duration, 'URL:', e.target.src);
                    }}
                    onCanPlay={(e) => {
                      console.log('Audio can play, URL:', e.target.src);
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Attachments */}
            {feedback.attachments && feedback.attachments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Attachments
                </Typography>
                <Stack spacing={1}>
                  {feedback.attachments.map((attachment, index) => {
                    const attachmentUrl = attachment.url.startsWith('http') 
                      ? attachment.url 
                      : `${window.location.origin}${attachment.url.startsWith('/') ? '' : '/'}${attachment.url}`;
                    return (
                      <Link
                        key={index}
                        href={attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Download />
                        {attachment.filename} ({(attachment.size / 1024).toFixed(1)} KB)
                      </Link>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Screenshot */}
            {feedback.screenshotUrl && (
              <Box sx={{ mt: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Screenshot
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    href={
                      feedback.screenshotUrl.startsWith('http') 
                        ? feedback.screenshotUrl 
                        : `${window.location.origin}${feedback.screenshotUrl.startsWith('/') ? '' : '/'}${feedback.screenshotUrl}`
                    }
                    download
                  >
                    Download
                  </Button>
                </Stack>
                <Box
                  sx={{
                    cursor: 'pointer',
                    display: 'inline-block',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                  onClick={() => setScreenshotDialogOpen(true)}
                >
                  <img
                    src={
                      feedback.screenshotUrl.startsWith('http') 
                        ? feedback.screenshotUrl 
                        : `${window.location.origin}${feedback.screenshotUrl.startsWith('/') ? '' : '/'}${feedback.screenshotUrl}`
                    }
                    alt="Screenshot"
                    style={{ 
                      maxWidth: '300px', 
                      maxHeight: '200px', 
                      display: 'block',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                
                {/* Screenshot Dialog */}
                <Dialog
                  open={screenshotDialogOpen}
                  onClose={() => setScreenshotDialogOpen(false)}
                  maxWidth="lg"
                  fullWidth
                >
                  <DialogContent sx={{ p: 0, textAlign: 'center', bgcolor: '#000' }}>
                    <img
                      src={
                        feedback.screenshotUrl.startsWith('http') 
                          ? feedback.screenshotUrl 
                          : `${window.location.origin}${feedback.screenshotUrl.startsWith('/') ? '' : '/'}${feedback.screenshotUrl}`
                      }
                      alt="Screenshot - Full Size"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '90vh',
                        objectFit: 'contain'
                      }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setScreenshotDialogOpen(false)}>Close</Button>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      href={
                        feedback.screenshotUrl.startsWith('http') 
                          ? feedback.screenshotUrl 
                          : `${window.location.origin}${feedback.screenshotUrl.startsWith('/') ? '' : '/'}${feedback.screenshotUrl}`
                      }
                      download
                    >
                      Download
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}
          </Paper>

          {/* Comments Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comments
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Comments List */}
            {feedback.comments && feedback.comments.length > 0 ? (
              <Stack spacing={2} sx={{ mb: 2 }}>
                {feedback.comments.map((comment, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Typography variant="body2" gutterBottom>
                        {comment.comment}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {comment.commentedBy
                          ? `${comment.commentedBy.firstName} ${comment.commentedBy.lastName}`
                          : 'Unknown'}{' '}
                        - {new Date(comment.commentedAt).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                No comments yet
              </Typography>
            )}

            {/* Add Comment Form */}
            {canComment() ? (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Add Comment"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Enter your comment..."
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  onClick={handleCommentSubmit}
                  disabled={submittingComment || !newComment.trim()}
                >
                  {submittingComment ? 'Submitting...' : 'Add Comment'}
                </Button>
              </Box>
            ) : (
              <Alert severity="info">Comments are disabled for closed feedback</Alert>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Editable Fields */}
          {canEdit() && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Edit Feedback</Typography>
                {!isEditing ? (
                  <IconButton onClick={handleEdit} size="small">
                    <Edit />
                  </IconButton>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <IconButton onClick={handleSave} size="small" color="primary" disabled={updating}>
                      <Save />
                    </IconButton>
                    <IconButton onClick={handleCancelEdit} size="small" disabled={updating}>
                      <Cancel />
                    </IconButton>
                  </Stack>
                )}
              </Stack>

              {isEditing ? (
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editData.status}
                      onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="New">New</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Fixed">Fixed</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                      <MenuItem value="Reopened" disabled={feedback.status === 'Closed'}>
                        Reopened
                      </MenuItem>
                      <MenuItem value="On Hold">On Hold</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Component</InputLabel>
                    <Select
                      value={editData.component}
                      onChange={(e) => setEditData({ ...editData, component: e.target.value })}
                      label="Component"
                    >
                      <MenuItem value="UI">UI</MenuItem>
                      <MenuItem value="Backend">Backend</MenuItem>
                      <MenuItem value="Database">Database</MenuItem>
                      <MenuItem value="API">API</MenuItem>
                      <MenuItem value="Integration">Integration</MenuItem>
                      <MenuItem value="Security">Security</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Environment"
                    value={editData.environment}
                    onChange={(e) => setEditData({ ...editData, environment: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Version"
                    value={editData.version}
                    onChange={(e) => setEditData({ ...editData, version: e.target.value })}
                  />
                   <FormControl fullWidth disabled={!canEditAssignedTo()}>
                    <InputLabel>Assigned To</InputLabel>
                    <Select
                      value={editData.assignedTo || 'Support Team'}
                      onChange={(e) => setEditData({ ...editData, assignedTo: e.target.value })}
                      label="Assigned To"
                    >
                      <MenuItem value="Support Team">Support Team</MenuItem>
                      {getSubmitterFullName() && (
                        <MenuItem value={getSubmitterFullName()}>{getSubmitterFullName()}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Typography variant="body1">{feedback.status}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Component
                    </Typography>
                    <Typography variant="body1">{feedback.component || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Environment
                    </Typography>
                    <Typography variant="body1">{feedback.environment || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Version
                    </Typography>
                    <Typography variant="body1">{feedback.version || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Assigned To
                    </Typography>
                    <Typography variant="body1">{feedback.assignedTo || 'Support Team'}</Typography>
                  </Box>
                </Stack>
              )}
            </Paper>
          )}

          {/* Shareable Link */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Shareable Link
            </Typography>
            <TextField
              fullWidth
              value={shareableLink}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={copyShareableLink} size="small">
                    <ContentCopy />
                  </IconButton>
                )
              }}
              size="small"
            />
          </Paper>

          {/* Status History */}
          {feedback.statusHistory && feedback.statusHistory.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Status History
              </Typography>
              <Timeline>
                {feedback.statusHistory.map((history, index) => (
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot color={getStatusColor(history.status)} />
                      {index < feedback.statusHistory.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="subtitle2">
                        {history.status}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {history.changedBy
                          ? `${history.changedBy.firstName} ${history.changedBy.lastName}`
                          : 'System'}{' '}
                        - {new Date(history.changedAt).toLocaleString()}
                      </Typography>
                      {history.notes && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {history.notes}
                        </Typography>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            </Paper>
          )}

          {/* Submitted Info */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Submitted By
            </Typography>
            <Typography variant="body2">
              {feedback.submittedBy
                ? `${feedback.submittedBy.firstName} ${feedback.submittedBy.lastName} (${feedback.submittedBy.email})`
                : 'Anonymous'}
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {new Date(feedback.createdAt).toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FeedbackDetail;

